import { ERevIntelDashboard, TRevIntelObjectType } from 'types/revIntel';

import { L10nRevenueIntelligence } from 'helpers/l10n/L10RevenueIntelligence';

import {
  COMPANY_QUICKVIEW_TABS,
  CONTACT_QUICKVIEW_TABS,
  DEAL_QUICKVIEW_TABS
} from './deals';

export const DEALS_DASHBOARD_COLUMNS = Object.freeze({
  DEAL_RISK: true,
  DEAL_HEALTH: true,
  AI_METHODOLOGY: true,
  ENGAGEMENT: true,
  DEAL: false
});

export const COMPANY_DASHBOARD_COLUMNS = Object.freeze({
  DEAL_RISK: false,
  AI_METHODOLOGY: false,
  DEAL_HEALTH: false,
  ENGAGEMENT: true,
  DEAL: true
});

export const CONTACT_DASHBOARD_COLUMNS = Object.freeze({
  DEAL_RISK: false,
  DEAL_HEALTH: false,
  AI_METHODOLOGY: false,
  ENGAGEMENT: true,
  DEAL: true
});

export const LEAD_DASHBOARD_COLUMNS = Object.freeze({
  DEAL_RISK: false,
  DEAL_HEALTH: false,
  AI_METHODOLOGY: false,
  ENGAGEMENT: true,
  DEAL: false
});

export const FORECAST_SUBMISSION_DASHBOARD_COLUMNS = Object.freeze({
  DEAL_RISK: false,
  DEAL_HEALTH: true,
  AI_METHODOLOGY: false,
  ENGAGEMENT: false,
  DEAL: false
});

export const DASHBOARD_TOTAL_COUNT_COLUMNS = Object.freeze({
  contact: 'contacts',
  lead: 'leads',
  company: 'accounts',
  deal: 'opportunities'
});

export const REV_INTEL_DASHBOARD_DETAILS: Record<
  ERevIntelDashboard,
  { title: string; objectsName: string }
> = {
  [ERevIntelDashboard.company]: {
    title: L10nRevenueIntelligence.companies.title,
    objectsName: L10nRevenueIntelligence.companies.objectsName
  },
  [ERevIntelDashboard.contact]: {
    title: L10nRevenueIntelligence.contacts.title,
    objectsName: L10nRevenueIntelligence.contacts.objectsName
  },
  [ERevIntelDashboard.deal]: {
    title: L10nRevenueIntelligence.deals.title,
    objectsName: L10nRevenueIntelligence.deals.objectsName
  },
  [ERevIntelDashboard.forecastSubmission]: {
    // Forecast submission is a deal selection dashboard. Same as deals dashboard with different default columns.
    title: L10nRevenueIntelligence.deals.title,
    objectsName: L10nRevenueIntelligence.deals.objectsName
  },
  [ERevIntelDashboard.lead]: {
    title: L10nRevenueIntelligence.leads.title,
    objectsName: L10nRevenueIntelligence.leads.objectsName
  }
} as const;

export const REV_INTEL_QUICKVIEW_EVENTS = {
  activity: 'ACTIVITY_TAB',
  properties: 'PROPERTIES_TAB',
  comments: 'COMMENTS_TAB',
  alerts: 'DEAL_HEALTH_TAB',
  'ai-analysis': 'AI_ANALYSIS_TAB',
  'ask-avoma': 'ASK_AVOMA_TAB',
  'ai-methodology': 'AI_METHODOLOGY_TAB'
} as const;

export const AI_METHODOLOGY_EVENTS = {
  VIEWED: 'VIEWED',
  ENABLED: 'ENABLED',
  UPDATED: 'UPDATED',
  NAVIGATED: 'NAVIGATED',
  PIPELINE_ASSIGNED: 'PIPELINE_ASSIGNED',
  FIELD_MAPPED: 'FIELD_MAPPED',
  TEMPLATE_SELECTED: 'TEMPLATE_SELECTED',
  SCORECARD_SELECTED: 'SCORECARD_SELECTED',
  DEAL_SCORING_SAVED: 'DEAL_SCORING_SAVED'
} as const;

interface IGetDashboardColumnsOptions {
  isNewDealHealthEnabled?: boolean;
}

export const getDashboardColumns = (
  dashboardKey: ERevIntelDashboard,
  { isNewDealHealthEnabled = false }: IGetDashboardColumnsOptions = {}
) => {
  const dealDashboardColumns = Object.freeze({
    ...DEALS_DASHBOARD_COLUMNS,
    DEAL_HEALTH: !isNewDealHealthEnabled
  });

  switch (dashboardKey) {
    case ERevIntelDashboard.forecastSubmission:
      return FORECAST_SUBMISSION_DASHBOARD_COLUMNS;
    case ERevIntelDashboard.contact:
      return CONTACT_DASHBOARD_COLUMNS;
    case ERevIntelDashboard.lead:
      return LEAD_DASHBOARD_COLUMNS;
    case ERevIntelDashboard.deal:
      return dealDashboardColumns;
    case ERevIntelDashboard.company:
      return COMPANY_DASHBOARD_COLUMNS;
    default:
      return dealDashboardColumns;
  }
};

// TODO: type is used in multiple places in the app. We should use ERevIntelDashboard instead
export const getRevIntelDashboardKeyFromType = (type: TRevIntelObjectType) => {
  switch (type) {
    case 'account':
      return ERevIntelDashboard.company;
    case 'contact':
      return ERevIntelDashboard.contact;
    case 'lead':
      return ERevIntelDashboard.lead;
    case 'opportunity':
    default:
      return ERevIntelDashboard.deal;
  }
};

export const getObjectTypeFromDashboardKey = (
  dashboardKey: ERevIntelDashboard
) => {
  switch (dashboardKey) {
    case ERevIntelDashboard.company:
      return 'account';
    case ERevIntelDashboard.contact:
      return 'contact';
    case ERevIntelDashboard.lead:
      return 'lead';
    case ERevIntelDashboard.deal:
    default:
      return 'opportunity';
  }
};

export const getFieldGroupByType = (type: string) => {
  switch (type) {
    case 'account':
      return 'crm_account_field_group';
    case 'contact':
      return 'crm_contact_field_group';
    case 'lead':
      return 'crm_lead_field_group';
    case 'opportunity':
    default:
      return 'crm_oppo_field_group';
  }
};

// Some columns have special names that are not user friendly. This function is used to get the user friendly name.
export const getRevIntelColumnLabel = (name: string) => {
  switch (name.toLowerCase()) {
    case 'email sent':
      return 'Emails Sent';
    case 'email received':
      return 'Emails Received';
    case 'email scheduled':
      return 'Emails Scheduled';
    case 'record type id':
      return 'Record Type';
    default:
      return name;
  }
};

export const getFirstQuickViewTab = (type: TRevIntelObjectType) => {
  switch (type) {
    case 'account':
      return COMPANY_QUICKVIEW_TABS[0].value;
    case 'contact':
      return CONTACT_QUICKVIEW_TABS[0].value;
    case 'opportunity':
    default:
      return DEAL_QUICKVIEW_TABS[0].value;
  }
};

export const getFixedColumns = (
  dashboardKey: ERevIntelDashboard,
  { isNewDealHealthEnabled = false }: IGetDashboardColumnsOptions = {}
) => {
  switch (dashboardKey) {
    case ERevIntelDashboard.company:
      return ['Company Name', 'Engagement', 'Deals'];
    case ERevIntelDashboard.contact:
      return ['Contact Name', 'Engagement', 'Deals', 'Email'];
    case ERevIntelDashboard.lead:
      return ['Lead Name', 'Engagement', 'Deals', 'Email'];
    case ERevIntelDashboard.deal:
    default:
      return isNewDealHealthEnabled
        ? ['Deal Name', 'Risk Score', 'Qualification Score', 'Engagement']
        : [
            'Deal Name',
            'Risk Score',
            'Deal Health',
            'Qualification Score',
            'Engagement'
          ];
  }
};

export const CONTACT_NAME_COLUMN = {
  uuid: 'contact-name-column',
  crmField: {
    uuid: 'contact-name-field',
    fieldLabel: 'Contact Name',
    externalId: 'contactname',
    dataType: 'string',
    fieldType: 'text',
    isReadOnly: false,
    syncDirection: 'bid',
    mappedAvFieldLabel: null,
    optionsJson: [],
    editableBy: 'everyone'
  },
  avField: null,
  sortOrder: null,
  order: -1,
  isDefault: true,
  groupName: 'Contact',
  objectType: 'crmleadfield',
  isEditable: true,
  isFilterable: false,
  sortKey: null,
  orderBy: null,
  filterKey: null,
  referenceKey: null,
  isSummarized: false
};

export const LEAD_NAME_COLUMN = {
  uuid: 'lead-name-column',
  crmField: {
    uuid: 'lead-name-field',
    fieldLabel: 'Lead Name',
    externalId: 'contactname',
    dataType: 'string',
    fieldType: 'text',
    isReadOnly: false,
    syncDirection: 'bid',
    mappedAvFieldLabel: null,
    optionsJson: [],
    editableBy: 'everyone'
  },
  avField: null,
  sortOrder: null,
  order: -1,
  isDefault: true,
  groupName: 'Lead',
  objectType: 'crmleadfield',
  isEditable: true,
  isFilterable: false,
  sortKey: null,
  orderBy: null,
  filterKey: null,
  referenceKey: null,
  isSummarized: false
};

// List of columns that cannot be removed from the dashboards
// dealname - deals dashboard first column
// name - deals dashboard second column
export const REV_INTEL_DEFAULT_COLUMNS_IDS = ['dealname', 'name'];
